import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../auth/AuthContext';
import { useActiveAccount } from '../../account/ActiveAccountContext';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import * as threadAccountApi from '../../api/threadAccount';
import { useAppTheme } from '../../styles/ThemeModeContext';
import { useDialog } from '../dialog/DialogProvider';
import { Avatar } from '../components/Avatar';
import { Badge } from '../components/feedback';
import {
  BrandLogo,
  ChevronDownIcon,
  LogoutIcon,
  MoonIcon,
  PlusIcon,
  SettingsIcon,
  SunIcon,
  UserIcon,
} from '../components/icons';

interface AppHeaderProps {
  nav?: ReactNode;
}

export function AppHeader({ nav }: AppHeaderProps) {
  const { logout } = useAuth();
  const { accounts, selectedAccount, selectedAccountId, selectAccount } =
    useActiveAccount();
  const { mode, toggleMode } = useAppTheme();
  const { confirm } = useDialog();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useOnClickOutside(menuRef, () => setOpen(false), open);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [open]);

  const profile = selectedAccount?.threadAccountProfileResponse;
  const displayName = profile?.threadUserName
    ? `@${profile.threadUserName}`
    : selectedAccount
      ? `계정 #${selectedAccount.id}`
      : '계정 선택';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleSwitch = (id: number) => {
    selectAccount(id);
    setOpen(false);
  };

  const handleAddAccount = async () => {
    setOpen(false);
    const ok = await confirm({
      title: 'Threads 계정 추가',
      message:
        '브라우저에 이미 Threads에 로그인한 계정이 있으면 그 계정으로 연동됩니다. 다른 계정을 추가하려면 threads.com에서 먼저 로그아웃한 뒤 계속을 눌러 주세요.',
      confirmText: '연동 계속',
      cancelText: '취소',
    });
    if (ok) {
      try {
        await threadAccountApi.openOAuthLoginForNewAccount();
      } catch (error) {
        await confirm({
          title: '계정 연결 실패',
          message: error instanceof Error ? error.message : '계정을 연결하지 못했습니다.',
          confirmText: '확인',
          tone: 'danger',
        });
      }
    }
  };

  return (
    <Bar>
      <Left>
        <Brand to="/board" aria-label="Auto Threads 콘텐츠 보드">
          <BrandLogo size={28} />
          <BrandText>Auto Threads</BrandText>
        </Brand>
        {nav}
      </Left>

      <Right>
        <ThemeButton
          type="button"
          onClick={toggleMode}
          aria-label={mode === 'dark' ? '라이트 테마로 전환' : '다크 테마로 전환'}
          title={mode === 'dark' ? '라이트 테마' : '다크 테마'}
        >
          {mode === 'dark' ? <SunIcon size={18} /> : <MoonIcon size={18} />}
        </ThemeButton>

        <Profile ref={menuRef} onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
        }}>
          <ProfileTrigger
            ref={triggerRef}
            type="button"
            aria-label={`Threads 계정 메뉴: ${displayName}`}
            aria-expanded={open}
            aria-controls={open ? 'account-menu' : undefined}
            onClick={() => setOpen((v) => !v)}
            $open={open}
          >
            <Avatar
              src={profile?.threadProfilePictureUrl}
              name={profile?.threadUserName}
              size={32}
            />
            <ProfileText>
              <ProfileName>{displayName}</ProfileName>
            </ProfileText>
            <Caret $open={open}>
              <ChevronDownIcon size={16} />
            </Caret>
          </ProfileTrigger>

          {open && (
            <Menu id="account-menu" aria-label="Threads 계정 메뉴">
              <MenuLabel>Threads 계정</MenuLabel>
              {accounts.length === 0 ? (
                <EmptyRow>
                  연동된 계정이 없습니다.
                  <button type="button" onClick={handleAddAccount}>
                    계정 연동하기
                  </button>
                </EmptyRow>
              ) : (
                <AccountList>
                  {accounts.map((acc) => {
                    const p = acc.threadAccountProfileResponse;
                    const active = acc.id === selectedAccountId;
                    return (
                      <AccountRow
                        key={acc.id}
                        $active={active}
                        aria-pressed={active}
                        onClick={() => handleSwitch(acc.id)}
                      >
                        <Avatar
                          src={p?.threadProfilePictureUrl}
                          name={p?.threadUserName}
                          size={34}
                        />
                        <RowText>
                          <RowName>
                            {p?.threadUserName
                              ? `@${p.threadUserName}`
                              : `계정 #${acc.id}`}
                          </RowName>
                          <RowSub>{p?.name ?? '이름 미설정'}</RowSub>
                        </RowText>
                        {active && <Badge $tone="primary">현재</Badge>}
                      </AccountRow>
                    );
                  })}
                </AccountList>
              )}

              <Divider />
              <MenuAction type="button" onClick={handleAddAccount}>
                <PlusIcon size={16} /> 계정 연동
              </MenuAction>
              <MenuItem to="/account" onClick={() => setOpen(false)}>
                <UserIcon size={16} /> 계정 설정
              </MenuItem>
              <MenuItem to="/settings" onClick={() => setOpen(false)}>
                <SettingsIcon size={16} /> 앱 설정
              </MenuItem>
              <MenuButton onClick={handleLogout}>
                <LogoutIcon size={16} /> 로그아웃
              </MenuButton>
            </Menu>
          )}
        </Profile>
      </Right>
    </Bar>
  );
}

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 72px;
  padding: 0 28px;
  background: ${({ theme }) => theme.colors.headerBg};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 768px) {
    padding: 0 12px;
    height: 64px;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
`;

const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;

  &:hover {
    opacity: 0.92;
  }
`;

const BrandText = styled.span`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;

  @media (max-width: 420px) {
    display: none;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ThemeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const Profile = styled.div`
  position: relative;
`;

const ProfileTrigger = styled.button<{ $open: boolean }>`
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 14rem;
  padding: 4px 8px 4px 4px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ $open, theme }) =>
    $open ? theme.colors.primarySoft : theme.colors.surfaceMuted};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primarySoft};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  @media (max-width: 560px) {
    padding: 3px;
  }
`;

const ProfileText = styled.div`
  text-align: left;
  line-height: 1.25;
  min-width: 0;

  @media (max-width: 560px) {
    display: none;
  }
`;

const ProfileName = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);

  @media (max-width: 560px) {
    display: none;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: min(300px, calc(100vw - 24px));
  max-height: calc(100dvh - 96px);
  overflow-y: auto;
  padding: 10px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  z-index: 40;
`;

const MenuLabel = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 6px 8px;
`;

const AccountList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 280px;
  overflow-y: auto;
`;

const AccountRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px;
  border-radius: ${({ theme }) => theme.radius.md};
  text-align: left;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : 'transparent'};

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primarySoft : theme.colors.surfaceMuted};
  }
`;

const RowText = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowName = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowSub = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};

  button {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    text-align: left;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: 8px 4px;
`;

const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceMuted};
  }
`;

const MenuAction = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 10px 8px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceMuted};
  }
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 10px 8px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.danger};

  &:hover {
    background: ${({ theme }) => theme.colors.dangerSoft};
  }
`;
